import { useState, useEffect, useCallback } from "react";
import { z } from "zod";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Plus } from "lucide-react";
import {
  useERPIntegrationsStore,
  type ERPIntegrationSync,
} from "@/store/erpIntegrationsStore";
import { ERPSyncSheet } from "@/components/erp/ERPSyncSheet";
import { SyncCard } from "@/components/settings/SyncCard";
import { SectionLabel } from "@/components/settings/SectionLabel";
import { ERPSettingsSkeleton } from "@/components/settings/ERPSettingsSkeleton";
import { useHasPermission } from "@/store/authStore";
import { Permission } from "@forgepoint/shared";
import { toast } from "sonner";
import { fetchApi } from "@/lib/api";

const PushEntityResultSchema = z.object({
  status: z.enum(["disabled", "unsupported"]).optional(),
  enqueued: z.number(),
});

const PushResponseSchema = z.object({
  companies: PushEntityResultSchema,
  people: PushEntityResultSchema,
  opportunities: PushEntityResultSchema,
  notes: PushEntityResultSchema,
  quotes: PushEntityResultSchema,
});

const ApiErrorSchema = z.object({ error: z.string() });

/**
 * ERP Integration settings panel for managing ERP sync jobs.
 * Uses the standard settings UX pattern with section labels, settings rows, and skeleton loading.
 * @returns ERP integration settings panel element
 */
export function ERPIntegrationSettingsPanel() {
  const erpIntegrationProviders = useERPIntegrationsStore(
    (state) => state.providers,
  );
  const fetchErpIntegrationProviders = useERPIntegrationsStore(
    (state) => state.fetchProviders,
  );
  const erpIntegrationSyncs = useERPIntegrationsStore((state) => state.syncs);
  const erpSyncsLoading = useERPIntegrationsStore(
    (state) => state.syncsLoading,
  );
  const erpSyncsError = useERPIntegrationsStore((state) => state.syncsError);
  const fetchErpIntegrationSyncs = useERPIntegrationsStore(
    (state) => state.fetchSyncs,
  );
  const canEdit = useHasPermission(Permission.SETTINGS_EDIT);

  const [isSyncSheetOpen, setIsSyncSheetOpen] = useState(false);
  const [editingSync, setEditingSync] = useState<ERPIntegrationSync | null>(
    null,
  );
  const [manualPushLoadingId, setManualPushLoadingId] = useState<string | null>(
    null,
  );
  const [manualPullLoadingId, setManualPullLoadingId] = useState<string | null>(
    null,
  );
  const [resetLoadingId, setResetLoadingId] = useState<string | null>(null);

  useEffect(() => {
    fetchErpIntegrationProviders();
  }, [fetchErpIntegrationProviders]);

  useEffect(() => {
    fetchErpIntegrationSyncs();
  }, [fetchErpIntegrationSyncs]);

  /**
   * Triggers a manual push for a given sync, enqueueing companies and people for export.
   * @param syncId - The ID of the sync to push
   */
  const handleManualPush = useCallback(async (syncId: string) => {
    setManualPushLoadingId(syncId);
    try {
      const response = await fetchApi(
        `integrations/erp/syncs/${syncId}/push`,
        "POST",
      );

      if (response.ok && response.data) {
        const summary = parsePushResponse(response.data);
        if (summary) {
          toast.success(`Push queued: ${summary}`);
        } else {
          toast.success("Push queued successfully.");
        }
      } else {
        toast.error("Failed to trigger manual push.");
      }
    } catch {
      toast.error("Failed to trigger manual push.");
    }
    setManualPushLoadingId(null);
  }, []);

  /**
   * Triggers a manual pull for a given sync, pulling companies and people from the ERP.
   * @param syncId - The ID of the sync to pull
   */
  const handleManualPull = useCallback(async (syncId: string) => {
    setManualPullLoadingId(syncId);
    try {
      const response = await fetchApi(
        `integrations/erp/syncs/${syncId}/pull`,
        "POST",
      );

      if (response.ok) {
        toast.success("Pull completed.");
      } else {
        toast.error("Failed to trigger manual pull.");
      }
    } catch {
      toast.error("Failed to trigger manual pull.");
    }
    setManualPullLoadingId(null);
  }, []);

  const handleResetLastSyncedAt = useCallback(async (syncId: string) => {
    setResetLoadingId(syncId);
    try {
      const response = await fetchApi(
        `integrations/erp/syncs/${syncId}/reset-last-synced-at`,
        "POST",
      );

      if (response.ok) {
        toast.success(
          "Sync progress reset. The next pull will run a full sync.",
        );
      } else {
        const error = ApiErrorSchema.safeParse(response.data);
        toast.error(
          error.success ? error.data.error : "Failed to reset sync progress.",
        );
      }
    } catch {
      toast.error("Failed to reset sync progress.");
    } finally {
      setResetLoadingId(null);
    }
  }, []);

  if (erpSyncsLoading) {
    return <ERPSettingsSkeleton />;
  }

  if (erpSyncsError) {
    return (
      <Card className="border-destructive/40 shadow-sm">
        <CardContent className="py-6 text-sm text-destructive">
          {erpSyncsError}
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <div className="space-y-6 pt-4">
        <div className="flex items-center justify-between">
          <SectionLabel id="erp-sync-jobs" title="Sync jobs" />
          {canEdit && (
            <Button size="sm" onClick={() => setIsSyncSheetOpen(true)}>
              <Plus className="mr-1.5 h-3.5 w-3.5" />
              New sync
            </Button>
          )}
        </div>

        {erpIntegrationSyncs.length === 0 ? (
          <Card className="overflow-hidden border-dashed shadow-none">
            <div className="px-4 py-10 text-center">
              <p className="text-sm font-medium">No syncs yet</p>
              <p className="mx-auto mt-1 max-w-sm text-xs text-muted-foreground">
                Connect a provider to start moving companies, people, and notes
                between Forgepoint and your ERP.
              </p>
              {canEdit && (
                <Button
                  size="sm"
                  variant="outline"
                  className="mt-4"
                  onClick={() => setIsSyncSheetOpen(true)}
                >
                  <Plus className="mr-1.5 h-3.5 w-3.5" />
                  New sync
                </Button>
              )}
            </div>
          </Card>
        ) : (
          <div className="space-y-4">
            {erpIntegrationSyncs.map((sync) => (
              <SyncCard
                key={sync.id}
                sync={sync}
                manualPushLoadingId={manualPushLoadingId}
                manualPullLoadingId={manualPullLoadingId}
                resetLoadingId={resetLoadingId}
                onEdit={setEditingSync}
                onManualPush={handleManualPush}
                onManualPull={handleManualPull}
                onResetLastSyncedAt={handleResetLastSyncedAt}
                canEdit={canEdit}
              />
            ))}
          </div>
        )}
      </div>

      <ERPSyncSheet
        mode="create"
        open={isSyncSheetOpen}
        onOpenChange={setIsSyncSheetOpen}
        providers={erpIntegrationProviders}
        onCreated={fetchErpIntegrationSyncs}
      />

      <ERPSyncSheet
        mode="edit"
        sync={editingSync}
        open={editingSync !== null}
        onOpenChange={(open) => {
          if (!open) {
            setEditingSync(null);
          }
        }}
        onSaved={fetchErpIntegrationSyncs}
      />
    </>
  );
}

/**
 * Safely parses the push API response and builds a summary string of enqueued counts,
 * excluding any entity types that are disabled.
 * @param data - The unknown response data from the push API
 * @returns A summary string like "3 companies, 2 people", or null if parsing fails
 */
function parsePushResponse(data: unknown): string | null {
  const result = PushResponseSchema.safeParse(data);
  if (!result.success) {
    return null;
  }

  const entries: Array<[string, z.infer<typeof PushEntityResultSchema>]> = [
    ["companies", result.data.companies],
    ["people", result.data.people],
    ["opportunities", result.data.opportunities],
    ["notes", result.data.notes],
    ["quotes", result.data.quotes],
  ];

  const parts = entries
    .filter(
      ([, entity]) =>
        entity.status !== "disabled" && entity.status !== "unsupported",
    )
    .map(([label, entity]) => `${entity.enqueued} ${label}`);

  if (parts.length === 0) {
    return null;
  }

  return parts.join(", ");
}
